import { useMemo } from 'react'
import { SketchElement } from '../../../../inspector'

/**
 * Hook that determines the target element for context menu actions.
 * If an element in the tree is already selected, use that.
 * Otherwise, use the leaf element of the first branch.
 */
export function useTargetElement(
  elementsList: SketchElement[],
  selectedElement: SketchElement | null
): SketchElement | null {
  return useMemo(() => {
    if (elementsList.length === 0) return null

    // There are two possible cases to find the final target:
    // - The selected element is in the element list or is one of their descendants then it should be the target.
    // - No selected element found, in which case we want the target (default target) to be the deepest element of
    //   the first branch (first branch == most on top on the canvas).
    // Because we need to look for the select element, we are forced to walk through all elements anyway but
    // the default target will always be the first element of the list or one of its descendants (at deepest level).

    let defaultTarget: SketchElement | null = null

    // Walk through the elements of one branch recursively
    const walkElements = (element: SketchElement): boolean => {
      // If this is the selected element, return it immediately
      if (element.identifier === selectedElement?.identifier) {
        return true
      }

      // If this element has no children, it's a candidate for deepest
      if (
        (!element.children || element.children.length === 0) &&
        !defaultTarget // The first default target we found is the one we want to use (i.e. the one in the first branch).
      ) {
        defaultTarget = element
        return false
      }

      // Continue looking into children
      for (const child of element.children) {
        const hasFoundSelectedElement = walkElements(child)
        // If we found the selected element in children, return it
        if (hasFoundSelectedElement) {
          return hasFoundSelectedElement
        }
      }

      return false
    }

    // Walk through all elements in the list
    for (const element of elementsList) {
      const hasFoundSelectedElement = walkElements(element)
      // If we found the selected element, return it
      if (hasFoundSelectedElement) {
        return selectedElement
      }
    }

    // If we haven't found any selected element, return the one we consider the target.
    return defaultTarget
  }, [elementsList, selectedElement])
}
